// Copyright 2021 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { ClearOutlined } from '@mui/icons-material';
import { createTheme, ThemeOptions } from '@mui/material';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { grey } from '@mui/material/colors';
import { LinkProps } from '@mui/material/Link';
import { PaletteColorOptions } from '@mui/material/styles/createPalette';

declare module '@mui/material/styles' {
  interface TypeBackground {
    gradiant: string;
  }
  interface SimplePaletteColorOptions {
    darker?: string;
  }
}

const success: PaletteColorOptions = {
  main: '#2e7d32',
};

const error: PaletteColorOptions = {
  main: '#bb0a0a',
};

const configureTheme = (): ThemeOptions => {
  const theme = createTheme({
    cssVariables: {
      colorSchemeSelector: 'class',
      cssVarPrefix: '',
    },
    colorSchemes: {
      light: {
        palette: {
          mode: 'light',
          primary: {
            main: '#006573',
          },
          background: {
            default: '#f6f6f6',
            gradiant: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), #f6f6f6)',
          },
          error,
          success,
        },
      },
      dark: {
        palette: {
          mode: 'dark',
          primary: {
            main: '#006573',
            // darker: '#001e21',
            darker: '#00181a',
          },
          background: {
            default: '#0e0e0e',
            paper: '#191919',
            gradiant: 'radial-gradient(at 50% 50%, #00181a, #000000)',
          },
          error,
          success,
        },
      },
    },
    shape: {
      borderRadius: 4,
    },
    spacing: 8,
    typography: {
      fontFamily: [
        'Metropolis',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Oxygen',
        'Helvetica Neue',
        'sans-serif',
        // 'Quicksand',
        // 'sans-serif',
        // 'Arial',
        // 'sans-serif',
      ].join(','),
    },
  });
  const opts: ThemeOptions = {
    components: {
      MuiStack: {
        defaultProps: {
          spacing: theme.spacing(2),
        },
      },
      MuiTypography: {
        styleOverrides: {
          overline: {
            color: theme.vars.palette.text.secondary,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          elevation: 1,
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          position: 'fixed',
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            borderTop: 0,
            borderRight: 0,
            borderLeft: 0,
          },
        },
      },
      MuiDrawer: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            // borderRadius: '20% 20% 0% 0%'
            background: 'none',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          padding: {
            padding: 0,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              borderLeft: '2px',
              borderStyle: 'solid',
              boxSizing: 'content-box',
            },
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
          color: 'primary',
        },
      },
      MuiChip: {
        defaultProps: {
          variant: 'filled',
          deleteIcon: <ClearOutlined />,
        },
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: theme.vars.palette.background.default,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            top: theme.spacing(0.5),
            padding: theme.spacing(1),
            margin: theme.spacing(2, 0, 2, 0),
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiIconButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            '&:hover': {
              background: 'none',
              color: theme.vars.palette.primary.main,
            },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            fontSize: '1rem',
          },
          asterisk: {
            color: 'darkred',
            fontWeight: 'bold',
            fontSize: 'x-large',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiFormControlLabel: {
        defaultProps: {
          slotProps: {
            typography: {
              noWrap: true,
            },
          },
        },
      },
      MuiInput: {
        defaultProps: {
          disableUnderline: true,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${grey['300']}`,
            borderRadius: theme.shape.borderRadius,
            top: theme.spacing(0.5),
            padding: theme.spacing(1),
            margin: theme.spacing(2, 0, 2, 0),
            outline: `1px solid transparent`,
            '&.Mui-focused': {
              border: `1px solid ${theme.vars.palette.primary.main}`,
              outline: `1px solid ${theme.vars.palette.primary.main}`,
            },
            '&.Mui-error': {
              border: `1px solid ${theme.vars.palette.error.main}`,
              outline: `1px solid ${theme.vars.palette.error.main}`,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '& .MuiButtonBase-root': {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
          input: {
            padding: 0,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: theme.spacing(-1),
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          positionEnd: {
            '&.MuiIconButton': {
              marginRight: 0,
            },
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          // popupIcon: <UnfoldMore color="disabled" />,
        },
        styleOverrides: {
          input: {
            padding: '0 !important',
          },
          inputRoot: {
            paddingBottom: theme.spacing(1),
            '&:has(.MuiChip-root)': {
              padding: 0,
              paddingBottom: 1,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          // IconComponent: () => <UnfoldMore color="disabled" />,
        },
      },
      MuiPaper: {
        defaultProps: {
          variant: 'elevation',
          elevation: 0,
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          content: {
            // flex: '1 1 auto',
            // width: '100%',
            overflow: 'hidden',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          flexContainer: {
            position: 'inherit',
            zIndex: 1,
          },
          indicator: {
            zIndex: 0,
            height: '100%',
            borderRadius: theme.vars.shape.borderRadius,
            backgroundColor: theme.vars.palette.background.paper,
          },
        },
      },
      MuiTab: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.Mui-selected': {
              color: theme.vars.palette.text.primary,
            },
          },
        },
      },
      MuiSnackbar: {
        defaultProps: {
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'bottom',
          },
        },
        styleOverrides: {
          root: {},
        },
      },
      MuiSnackbarContent: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiAlert: {},
      MuiAvatar: {
        defaultProps: {
          variant: 'circular',
        },
        styleOverrides: {
          root: {
            // borderRadius: 12,
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            marginTop: theme.spacing(-1),
            borderCollapse: 'separate',
            // if borderCollapse: "separate",
            borderSpacing: theme.spacing(0, 1),
            padding: theme.spacing(1),
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            textWrap: 'nowrap',
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {},
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            td: {
              backgroundColor: theme.vars.palette.background.paper,
              // if borderCollapse: "collapse",
              // borderTop: `solid ${theme.vars.palette.background.default} 10px`,
              // borderBottom: `solid ${theme.vars.palette.background.default} 10px`,
              '&:first-of-type': {
                borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
              },
              '&:last-child': {
                borderTopRightRadius: `${theme.shape.borderRadius}px`,
                borderBottomRightRadius: `${theme.shape.borderRadius}px`,
              },
            },
            th: {
              backgroundColor: theme.vars.palette.background.paper,
              // if borderCollapse: "collapse",
              // borderTop: `solid ${theme.vars.palette.background.default} 10px`,
              // borderBottom: `solid ${theme.vars.palette.background.default} 10px`,
              '&:first-of-type': {
                borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
              },
              '&:last-child': {
                borderTopRightRadius: `${theme.shape.borderRadius}px`,
                borderBottomRightRadius: `${theme.shape.borderRadius}px`,
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            border: 'none',
          },
        },
      },
    },
  };
  // bypass: Error: MUI: `vars` is a private field used for CSS variables support.
  // Please use another name.
  // @ts-ignore
  delete theme.vars;
  return createTheme(theme, opts);
};

export const theme = configureTheme();
